import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import uuidv4 from 'uuid/v4';

import { compact, find, forEach, has, isEqual, map, some } from 'lodash-es';
import { getLabel, getNewTitle } from '../../Utils/utils';

import SelectButtonBlock from '../Common/Blocks/SelectButtonBlock';
import ButtonAction from '../Common/Blocks/ButtonAction';
import { ActionButtonEnum } from '../Common/Blocks/ButtonAction/ButtonAction.enum';

import QuestionnaireBlock from '../QuestionnaireBlock/QuestionnaireBlock';
import { questionnaireShape } from '../QuestionnaireBlock/QuestionnaireBlock.shape';

import './MultipleQuestionnairesBlock.scss';

const MultipleQuestionnairesBlock = ({
  blockData,
  blockType,
  eventEmitter,
  strings,
  willResetForm,
  onResetForm,
  validateFormGraduatedLoans,
  i18n,
  canAdd,
}) => {
  const [stateBlock, setStateBlock] = useState(blockData[blockType]);
  const [freezeStateBlock, setFreezeStateBlock] = useState(blockData[blockType]);

  const onResetFormLocal = () => {
    setStateBlock(blockData[blockType]);
    setFreezeStateBlock(blockData[blockType]);

    onResetForm(blockData[blockType]);
  };

  const getNewEmptyBlock = (block, newSequence, relationCode) => {
    let newId = `${uuidv4()}`;

    switch (blockType) {
      case 'loans':
        newId = `loan_${newId}`;
        break;
      case 'quotations':
        newId = `party_${newId}`;
        break;
      default:
        newId = `${blockType}_${newId}`;
    }

    return {
      ...block,
      id: newId,
      sequence: newSequence,
      parts: map(block.parts, part => ({
        ...part,
        questions: map(part.questions, q => {
          if (blockType === 'quotations' && q.code === 'relation') {
            return { ...q, value: relationCode };
          }
          return { ...q, value: undefined };
        }),
      })),
    };
  };

  const onClickAdd = relationCode => {
    setStateBlock([
      ...stateBlock,
      getNewEmptyBlock(
        blockData.generics?.[relationCode] || blockData[blockType][0],
        freezeStateBlock.length + 1,
        relationCode
      ),
    ]);
  };

  const onClickRemove = id => () => {
    const newStateBlock = stateBlock.filter(block => block.id !== id);

    setStateBlock(newStateBlock);

    eventEmitter({ id, isValid: null, answers: null });
  };

  useEffect(() => {
    // Use freezeStateBlock to freeze initial state, so we can add/remove blocks/questionnaires to stateBlock
    // If blockData is updated (save or reset), so different from freezeStateBlock, it will be updated
    if (!isEqual(blockData[blockType], freezeStateBlock)) {
      setStateBlock(blockData[blockType]);
      setFreezeStateBlock(blockData[blockType]);
    }
  }, [blockData[blockType]]);

  // Get current number of covered for each relation type
  const getCurrentNumberCoveredTypes = () => {
    const relationsCurrent = {};

    forEach(stateBlock, block => {
      some(block?.parts, part =>
        some(part?.questions, question => {
          if (question?.code === 'relation') {
            if (question?.value) {
              if (!has(relationsCurrent, question?.value)) {
                relationsCurrent[question?.value] = { current: 0 };
              }

              relationsCurrent[question?.value].current++;
            }

            return true;
          }

          return false;
        })
      );
    });

    return relationsCurrent;
  };

  const currentNumberCoveredTypes = getCurrentNumberCoveredTypes();

  const head = (block, idx) => {
    let canRemovePartie = idx && canAdd;

    const relations = blockData.relations?.length ? blockData.relations : null;

    if (relations) {
      // Get relation type for this partieItem
      const typeRelation = compact(
        map(block?.parts, part => find(part?.questions, question => question?.code === 'relation')).flat()
      )[0];

      // Find relation
      const relation = find(relations, { code: typeRelation?.value });
      // Find relation count
      const relationNumber = currentNumberCoveredTypes?.[typeRelation?.value];

      // If current relation count if below or equal to min, no possibility to remove it
      if (relation?.min != null && relationNumber && relationNumber.current <= relation.min) {
        canRemovePartie = false;
      }
    }

    return block.displayTitle ? (
      <div className="head">
        <span className="title" role="presentation">
          {getNewTitle({
            title: getLabel({ label: block?.title, strings, defaultCode: `editsalesroute.${blockType}.block.title` }),
            isTitleMultiple: block?.isTitleMultiple,
            idx: idx + 1,
          })}
        </span>
        {canRemovePartie ? (
          <ButtonAction
            disabled={block.disabled}
            typeAction={ActionButtonEnum.REMOVE}
            action={onClickRemove(block.id)}
            label={getLabel({
              label: blockData.stringsBlock?.remove,
              strings,
              defaultCode: `editsalesroute.${blockType}.button.remove`,
              defaultString: 'Supprimer',
            })}
          />
        ) : (
          <></>
        )}
      </div>
    ) : (
      <></>
    );
  };

  const getAddActionComponent = () => {
    let options = blockData.relations?.length ? blockData.relations : null;
    let isDisabled = false;

    if (blockType === 'quotations' && options) {
      // Disable options that have read their max
      // Disable select+add button if all options are disabled
      options = map(options, option => {
        const newOption = { ...option };
        newOption.isDisabled = false;

        if (option.max != null) {
          const relationNumber = currentNumberCoveredTypes?.[option.code];

          // If max is 0, or if current relation count is over or equal to max, no possibility to add one
          if (option.max === 0 || (relationNumber && relationNumber.current >= option.max)) {
            newOption.isDisabled = true;
          }
        }

        return newOption;
      });

      // If all are disabled, disable select and button too!
      isDisabled = !some(
        options,
        option => !has(option, 'isDisabled') || option.isDisabled === (null || undefined || false) || option.max == null
      );
    }

    return (
      <SelectButtonBlock
        options={options}
        disabled={isDisabled}
        placeholder="Type de relation"
        label={getLabel({
          label: blockData.stringsBlock?.add,
          strings,
          defaultCode: `editsalesroute.${blockType}.button.add`,
          defaultString: 'Ajouter',
        })}
        eventEmitter={onClickAdd}
      />
    );
  };

  return (
    <>
      {map(stateBlock, (block, idx) => (
        <div className="multiple-questionnaires-block" key={`${blockType}_${block.id}`}>
          {head(block, idx)}
          <QuestionnaireBlock
            questionnaire={block}
            id={block.id}
            eventEmitter={eventEmitter}
            willResetForm={willResetForm}
            onResetForm={onResetFormLocal}
            graduatedLoans={
              blockType === 'loans' ? blockData.graduatedLoans[block.id] || blockData.graduatedLoans.generic : null
            }
            validateFormGraduatedLoans={validateFormGraduatedLoans}
            i18n={i18n}
            strings={strings}
          />
        </div>
      ))}
      {canAdd ? getAddActionComponent() : <></>}
    </>
  );
};

MultipleQuestionnairesBlock.propTypes = {
  blockData: PropTypes.objectOf(PropTypes.arrayOf(questionnaireShape)).isRequired,
  blockType: PropTypes.string.isRequired,
  eventEmitter: PropTypes.func.isRequired,
  strings: PropTypes.object.isRequired,
  willResetForm: PropTypes.bool,
  onResetForm: PropTypes.func,
  validateFormGraduatedLoans: PropTypes.func,
  i18n: PropTypes.object,
  canAdd: PropTypes.bool,
};

MultipleQuestionnairesBlock.defaultProps = {
  willResetForm: false,
  onResetForm: () => {},
  validateFormGraduatedLoans: undefined,
  i18n: {},
  canAdd: true,
};

export default MultipleQuestionnairesBlock;
